import { type TableColumn } from 'src/shared/components/Table/Table';
import { useTranslation } from 'react-i18next';
import { type FlexflowEmployeeDataToDisplay } from './types';
import { type FlexflowCardStatus } from '../types';
import { type Employee } from 'src/shared/types/employee';
import { useEffect } from 'react';
import { CheckboxCell, HeaderCheckboxCell } from 'src/shared/components/Table/cells/CheckboxCell';
import { useForm } from 'react-final-form';
import ContractStatusBeforeInvitation from './ContractStatusBeforeInvitation';
import ContractFlexflowStatusAfterInvitation, { type ContractPayflowStatus } from './ContractStatusAfterInvitation';
import { Link } from 'react-router-dom';
import { IconWrapper, PencilIcon } from './styles';
import { YesNoCellWithColors } from 'src/shared/components/Table/cells/YesNoCellWithColors';
import { useGetUserHoldingQuery } from 'src/shared/api/holding';
import { checkIfFrWalletWasEverEnabled, checkIfSbWasEverEnabled } from 'src/shared/utils/flexflow';

const formTableName = 'table';
const selectedAccessor = 'isSelected';

export const useGetColumns = (
  data: Array<FlexflowEmployeeDataToDisplay | Employee>,
  isRequestedAndActiveTab: boolean
): Array<TableColumn<FlexflowEmployeeDataToDisplay | Employee>> => {
  const { t } = useTranslation(['common', 'flexflow']);
  const form = useForm();
  const { data: holdingData } = useGetUserHoldingQuery();

  useEffect(() => {
    form.change('table', []);
  }, [data, form]);

  const frWalletEverEnabledForCompany = checkIfFrWalletWasEverEnabled(holdingData?.settings?.flexflow);
  const sbEverEnabled = checkIfSbWasEverEnabled(holdingData?.settings?.flexflow);
  const showFrAccumulatedColumns = frWalletEverEnabledForCompany;
  const showSocialBenefitColumns = sbEverEnabled;

  const columnsWithBooleans: Array<TableColumn<FlexflowEmployeeDataToDisplay | Employee> | boolean> = [
    {
      accessor: 'flexflowDetails',
      title: t('table.flexflow details', { ns: 'flexflow' }),
      headerCellProps: { sx: { width: '1%', whiteSpace: 'nowrap' } },
      renderCell: (row: FlexflowEmployeeDataToDisplay | Employee) => {
        const payflowContractId = row.id;
        return (
          <Link to={`/flexflow/employees/${payflowContractId}`}>
            <IconWrapper>
              <PencilIcon />
            </IconWrapper>
          </Link>
        );
      }
    },
    {
      accessor: 'flexflowStatus',
      title: t('table.flexflow status', { ns: 'flexflow' }),
      headerCellProps: { sx: { width: '200px', whiteSpace: 'nowrap' } },
      renderCell: (row: FlexflowEmployeeDataToDisplay | Employee) => {
        if (!isRequestedAndActiveTab) {
          return <ContractStatusBeforeInvitation flexflowStatus={row.invitationStatus} />;
        }

        const flexflowRow = row as FlexflowEmployeeDataToDisplay;
        const payflowStatus = flexflowRow.status;
        const rowFlexflowCardStatus = flexflowRow.card.status;
        return (
          <ContractFlexflowStatusAfterInvitation
            payflowStatus={payflowStatus as unknown as ContractPayflowStatus}
            flexflowCardStatus={rowFlexflowCardStatus as FlexflowCardStatus}
            cardSuspendedBySwan={Boolean(flexflowRow.card?.physicalCard?.suspendedBySwanAt)}
          />
        );
      }
    },
    {
      accessor: 'fullName',
      title: t('table.full name'),
      getTableCellProps: () => ({ sx: { fontWeight: 600 } }),
      renderCell: (row: FlexflowEmployeeDataToDisplay | Employee) => {
        return `${row.firstName} ${row.lastName}`;
      }
    },
    {
      accessor: 'personalId',
      title: t('table.personal id')
    },
    {
      accessor: 'payrollId',
      title: t('table.payroll id')
    },
    isRequestedAndActiveTab && {
      accessor: 'isEligible',
      title: t('table.isEligibleFlexflow', { ns: 'flexflow' }),
      renderCell: (row: FlexflowEmployeeDataToDisplay | Employee) => {
        return <YesNoCellWithColors value={row.isEligible} />;
      }
    },
    ...(showFrAccumulatedColumns
      ? [
          {
            accessor: 'frWalletOn',
            title: t('table.acumulado on', { ns: 'flexflow' }),
            renderCell: (row: FlexflowEmployeeDataToDisplay | Employee) => {
              const value = Boolean((row as FlexflowEmployeeDataToDisplay).settings?.frWalletAccrualEnabled);
              return <YesNoCellWithColors value={value} />;
            }
          }
        ]
      : []),
    ...(showSocialBenefitColumns
      ? [
          {
            accessor: 'sbAssigned',
            title: t('table.social benefit assigned', { ns: 'flexflow' }),
            renderCell: (row: FlexflowEmployeeDataToDisplay | Employee) => {
              const flexflowRow = row as FlexflowEmployeeDataToDisplay;
              const sbFoodAccrual = flexflowRow.settings?.sbFoodAccrual ?? 0;
              const sbTransportAccrual = flexflowRow.settings?.sbTransportAccrual ?? 0;
              return <YesNoCellWithColors value={sbFoodAccrual > 0 || sbTransportAccrual > 0} />;
            }
          }
        ]
      : [])
  ];
  const columns = columnsWithBooleans.filter(el => Boolean(el)) as Array<
    TableColumn<FlexflowEmployeeDataToDisplay | Employee>
  >;
  columns.unshift({
    accessor: selectedAccessor,
    headerCellProps: { sx: { width: '1%', whiteSpace: 'nowrap' } },
    getTableCellProps: () => ({ sx: { width: '1%', whiteSpace: 'nowrap' } }),
    renderHeaderCell: () => (
      <HeaderCheckboxCell accessor={selectedAccessor} rowsCount={data.length} formTableName={formTableName} />
    ),
    renderCell: (_, index) => <CheckboxCell accessor={`${formTableName}[${index}].${selectedAccessor}`} />
  });
  return columns;
};
